import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { MoreVertical, Pencil, Trash2 } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import {
  BillingCycleStatusBadge,
  FundingSourceBadge,
} from "./BillingCycleStatusBadge";
import { formatDateTime } from "@/lib/utils";
import { formatCurrency } from "@/lib/currency";
import type { BillingCycle } from "@/types/billing-cycle";
import type { OrderSummary } from "@/services/admin-order.service";
import {
  BILLING_CYCLE_COLUMNS,
  useBillingCycleColumnsStore,
  type BillingCycleColumnId,
} from "@/stores/billingCycleColumnsStore";

/**
 * How wide each column is drawn.
 *
 * Kept here rather than in the store because a width is presentation, not preference — the store
 * holds only what gets written to localStorage. Typing it as a full `Record` over the id union is
 * the point: adding a column to `BILLING_CYCLE_COLUMNS` without giving it a width is a compile
 * error, not a column that silently collapses.
 *
 * Fixed widths (not %) for the same reason as the resource and payment tables: resource name,
 * tenant and user need room to show full ids/emails without truncating, and the excess becomes a
 * horizontal scrollbar instead of squeezing every column into the viewport.
 */
const COLUMN_WIDTHS: Record<BillingCycleColumnId, string> = {
  index: "w-14",
  resource: "w-72",
  status: "w-27.5",
  funding: "w-24",
  order: "w-44",
  tenant: "w-64",
  user: "w-55",
  period: "w-52",
  amount: "w-44",
  renewal: "w-36",
  createdAt: "w-32.5",
  actions: "w-20",
};

/**
 * The Order column's cell: the order number an operator quotes, with the order type under it.
 *
 * Three states, and they are genuinely different — collapsing them into one dash is what makes an
 * operator open a ticket about "missing orders":
 *
 * - **no order to show** — the cycle is funded by a sign-up bonus or an ownership transfer, so
 *   `sourceReferenceId` points at a bonus lot or the previous cycle, not an order. A dash is the
 *   truth here and there is nothing to wait for.
 * - **still resolving** — the lookup runs as a second request after the page lands, so there is a
 *   moment where the row exists and its order does not. A skeleton says "wait", a dash would lie.
 * - **resolved but absent** — the id named an order that no longer exists. The raw id is shown
 *   rather than a dash, because that is the only thing left to search the database with.
 */
function OrderCell({
  cycle,
  order,
  isLoading,
  onOpen,
}: {
  cycle: BillingCycle;
  order?: OrderSummary;
  isLoading: boolean;
  onOpen: (orderId: string) => void;
}) {
  // Funding source, not the presence of the id: a Trial or Transfer cycle always carries a
  // sourceReferenceId, it just is not an order id. Asking the lookup to resolve it would be a
  // guaranteed miss, and rendering it would put a bonus-lot id under a column headed "Order".
  if (cycle.fundingSource !== "Payment" || !cycle.sourceReferenceId) {
    return <span className="text-muted-foreground">—</span>;
  }

  if (order) {
    return (
      <div className="min-w-0">
        <button
          onClick={() => onOpen(order.id)}
          title={order.orderNumber}
          className="block w-full cursor-pointer truncate text-left font-medium text-primary hover:underline"
        >
          {order.orderNumber}
        </button>
        <div className="truncate text-xs text-muted-foreground">
          {order.type}
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-1">
        <Skeleton className="h-4 w-28" />
        <Skeleton className="h-3 w-16" />
      </div>
    );
  }

  return (
    <p
      title={`Order ${cycle.sourceReferenceId} was not found`}
      className="truncate text-xs text-muted-foreground"
    >
      {cycle.sourceReferenceId}
    </p>
  );
}

interface BillingCycleTableProps {
  cycles: BillingCycle[];
  /** Row number of the first cycle in `cycles`, e.g. (page - 1) * pageSize. */
  startIndex?: number;
  /**
   * Orders for this page, keyed by order id, resolved by the page from each cycle's
   * `sourceReferenceId`. Absent entries are normal: only a `Payment`-funded cycle references an
   * order at all.
   */
  orders?: Map<string, OrderSummary>;
  /** The order lookup is still in flight. Drives the placeholder in the Order column only. */
  isOrdersLoading?: boolean;
  /**
   * Rows are on their way and there is nothing to show yet — the first load of the screen. Without
   * this the region fell back to the empty state and read "No billing cycles found" about a query
   * still in flight.
   */
  isLoading?: boolean;
}

export function BillingCycleTable({
  cycles,
  startIndex = 0,
  orders,
  isOrdersLoading = false,
  isLoading = false,
}: BillingCycleTableProps) {
  const navigate = useNavigate();
  const [cycleToDelete, setCycleToDelete] = useState<BillingCycle | null>(null);

  // Which columns are on screen. Subscribed here, set from the page's Columns menu — both talk to
  // the same store rather than threading props through.
  //
  // Subscribing to `hidden` and deriving the predicate locally, NOT subscribing to the store's own
  // `isVisible`: that one is a stable function reading the latest state, so a component that
  // subscribes to it re-renders for nothing that changed and never re-renders when a column is
  // toggled. `hidden` is the value that actually moves.
  const hiddenColumns = useBillingCycleColumnsStore((state) => state.hidden);
  const isVisible = (id: BillingCycleColumnId) => !hiddenColumns.includes(id);

  // TODO: no delete endpoint exists for billing cycles yet. Confirming only closes the dialog —
  // wire the mutation here once the API lands, and switch ConfirmDialog to its async mode
  // (`isPending`) so the spinner covers the request.
  const handleConfirmDelete = () => {
    setCycleToDelete(null);
  };

  // Placeholder rows rather than a collapsed strip: the region keeps the height it will have once
  // the rows land, so the toolbar above and the pagination below do not jump when they do.
  if (isLoading) {
    return (
      <div className="min-h-0 flex-1 space-y-3 overflow-hidden rounded-md border p-3">
        {Array.from({ length: 8 }).map((_, i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  if (cycles.length === 0) {
    return (
      <div className="py-12 text-center">
        <p className="text-muted-foreground">No billing cycles found</p>
      </div>
    );
  }

  return (
    /*
      `[&>div]:overflow-visible` targets the wrapper `components/ui/table.tsx` puts around every
      table (`<div class="relative w-full overflow-auto">`), and it is what makes the horizontal
      scrollbar reachable. That wrapper is as tall as the whole table, so while IT owned the
      horizontal overflow its scrollbar rendered below the last row — off the bottom of the
      viewport, where nobody could reach it. Switching it off hands both axes to this div, the one
      with a bounded height, so the bar sits at the bottom of the visible table. It also repairs the
      sticky header below: `position: sticky` resolves against the nearest scrollport, and while
      that was the inner wrapper — a div that never scrolls vertically — the header was declared
      sticky and simply scrolled away anyway. Same change as the resource and payment lists.
    */
    <div className="scrollbar-thin min-h-0 flex-1 overflow-auto rounded-md border [&>div]:overflow-visible">
      <Table className="table-fixed border-collapse [&_td:not(:last-child)]:border-r [&_th:not(:last-child)]:border-r">
        {/* Generated from BILLING_CYCLE_COLUMNS rather than written out, so a hidden column cannot
            leave its <col> behind — which would shift every width after it by one and is the one
            drift the browser gives no error for. A fixed-layout table is widened to the sum of
            these widths, so no `min-w` is needed to make it overflow — only a scrollport that puts
            the bar somewhere visible. */}
        <colgroup>
          {BILLING_CYCLE_COLUMNS.filter((column) => isVisible(column.id)).map(
            (column) => (
              <col key={column.id} className={COLUMN_WIDTHS[column.id]} />
            ),
          )}
        </colgroup>
        <TableHeader className="sticky top-0 z-30 bg-card">
          <TableRow>
            {isVisible("index") && (
              <TableHead className="text-center">STT</TableHead>
            )}
            {isVisible("resource") && <TableHead>Resource</TableHead>}
            {isVisible("status") && <TableHead>Status</TableHead>}
            {isVisible("funding") && <TableHead>Funding</TableHead>}
            {isVisible("order") && <TableHead>Order</TableHead>}
            {isVisible("tenant") && <TableHead>Tenant</TableHead>}
            {isVisible("user") && <TableHead>User</TableHead>}
            {isVisible("period") && <TableHead>Period</TableHead>}
            {isVisible("amount") && <TableHead>Amount</TableHead>}
            {isVisible("renewal") && <TableHead>Renewal</TableHead>}
            {isVisible("createdAt") && <TableHead>Created At</TableHead>}
            {isVisible("actions") && (
              <TableHead className="sticky right-0 z-20 bg-card text-center shadow-[inset_1px_0_0_0_var(--border)]">
                Actions
              </TableHead>
            )}
          </TableRow>
        </TableHeader>
        <TableBody>
          {cycles.map((cycle, index) => (
            <TableRow key={cycle.id}>
              {isVisible("index") && (
                <TableCell className="text-center text-muted-foreground">
                  {startIndex + index + 1}
                </TableCell>
              )}
              {isVisible("resource") && (
                <TableCell>
                  <div className="space-y-1">
                    {cycle.resourceName ? (
                      <button
                        onClick={() =>
                          navigate(`/admin/resources/${cycle.resourceId}`)
                        }
                        title={cycle.resourceName}
                        className="block max-w-full cursor-pointer truncate text-left font-medium text-primary hover:underline"
                      >
                        {cycle.resourceName}
                      </button>
                    ) : (
                      // The cycle outlives the resource row, so a deleted resource is normal
                      // history rather than a data fault - show the id instead of an empty cell.
                      <p className="truncate text-xs text-muted-foreground">
                        {cycle.resourceId}
                      </p>
                    )}
                    {cycle.providerResourceId && (
                      // Same "UUID: ..." line the resource table shows - it is the id an operator
                      // pastes into WiService. Absent while the resource is Provisioning/Error, or
                      // once the resource row is gone.
                      <p className="truncate text-xs text-muted-foreground">
                        UUID: {cycle.providerResourceId}
                      </p>
                    )}
                  </div>
                </TableCell>
              )}
              {isVisible("status") && (
                <TableCell>
                  <BillingCycleStatusBadge status={cycle.status} />
                  {cycle.cancellationReason && (
                    // Native title rather than the Radix tooltip: this app mounts no global
                    // TooltipProvider, and one reason string does not justify wrapping the table.
                    <p
                      title={cycle.cancellationReason}
                      className="mt-1 cursor-help truncate text-xs text-muted-foreground"
                    >
                      {cycle.cancellationReason}
                    </p>
                  )}
                </TableCell>
              )}
              {isVisible("funding") && (
                <TableCell>
                  <FundingSourceBadge fundingSource={cycle.fundingSource} />
                </TableCell>
              )}
              {isVisible("order") && (
                <TableCell>
                  <OrderCell
                    cycle={cycle}
                    order={
                      cycle.sourceReferenceId
                        ? orders?.get(cycle.sourceReferenceId)
                        : undefined
                    }
                    isLoading={isOrdersLoading}
                    onOpen={(orderId) =>
                      navigate(`/admin/orders/${orderId}`)
                    }
                  />
                </TableCell>
              )}
              {isVisible("tenant") && (
                <TableCell>
                  <div className="truncate">{cycle.tenantName ?? "—"}</div>
                  <div className="truncate text-xs text-muted-foreground">
                    {cycle.tenantId}
                  </div>
                </TableCell>
              )}
              {isVisible("user") && (
                <TableCell>
                  <div className="truncate">{cycle.userEmail ?? "—"}</div>
                  <div className="truncate text-xs text-muted-foreground">
                    {cycle.userPhoneNumber ?? "—"}
                  </div>
                </TableCell>
              )}
              {isVisible("period") && (
                <TableCell>
                  {/* Fixed label column so both dates line up despite "Start"/"End" differing in width. */}
                  <div className="grid grid-cols-[2.2rem_1fr] gap-x-1 text-xs">
                    <span className="text-muted-foreground">Start</span>
                    <span className="truncate">
                      {formatDateTime(cycle.cycleStart)}
                    </span>
                    <span className="text-muted-foreground">End</span>
                    <span className="truncate">
                      {formatDateTime(cycle.cycleEnd)}
                    </span>
                  </div>
                </TableCell>
              )}
              {isVisible("amount") && (
                <TableCell>
                  <div className="truncate">
                    {formatCurrency(cycle.amount, cycle.currency)}
                  </div>
                  {/* The TERM, not the frequency. `amount` is the whole period's total, so pairing
                      it with "Monthly" read as a monthly rate — and on a 12-month cycle that is a
                      year's money under a monthly label. `frequency` also cannot express the 24- and
                      36-month commitments, which record as "Monthly" too. Day-term cycles have no
                      whole month to show, so they keep the frequency. */}
                  <div className="truncate text-xs text-muted-foreground">
                    {cycle.termMonths > 0
                      ? `${cycle.termMonths} ${cycle.termMonths === 1 ? "month" : "months"}`
                      : cycle.frequency}
                  </div>
                </TableCell>
              )}
              {isVisible("renewal") && (
                <TableCell>
                  <Badge
                    variant="outline"
                    className={`px-1.5 py-0 text-[11px] ${
                      cycle.autoRenew
                        ? "bg-ok-soft text-ok-ink"
                        : "bg-secondary text-muted-foreground"
                    }`}
                  >
                    {cycle.autoRenew ? "Auto" : "Manual"}
                  </Badge>
                  {cycle.billingAttempts > 0 && (
                    // Retries are the signal an operator is hunting when a renewal did not take.
                    <div className="mt-1 truncate text-xs text-warn-ink">
                      {cycle.billingAttempts} attempt(s)
                    </div>
                  )}
                </TableCell>
              )}
              {isVisible("createdAt") && (
                <TableCell className="text-muted-foreground">
                  {formatDateTime(cycle.createdAt)}
                </TableCell>
              )}
              {isVisible("actions") && (
                <TableCell className="sticky right-0 z-10 bg-card shadow-[inset_1px_0_0_0_var(--border)]">
                  <div className="flex items-center justify-center">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="cursor-pointer"
                        >
                          <MoreVertical className="h-4 w-4" />
                          <span className="sr-only">Open menu</span>
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          className="cursor-pointer"
                          // The expiry screen, carrying this exact cycle: the row may be a closed
                          // historical one, so the page must not be left to guess which was meant.
                          onClick={() =>
                            navigate(
                              `/admin/resources/${cycle.resourceId}/expiry?cycleId=${cycle.id}`,
                            )
                          }
                        >
                          <Pencil className="mr-2 h-4 w-4" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          className="cursor-pointer text-destructive-ink focus:text-destructive-ink"
                          onClick={() => setCycleToDelete(cycle)}
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <ConfirmDialog
        open={cycleToDelete !== null}
        onOpenChange={(open) => !open && setCycleToDelete(null)}
        title="Delete this billing cycle?"
        description={
          cycleToDelete
            ? `The cycle for "${cycleToDelete.resourceName ?? cycleToDelete.resourceId}" would be removed. The delete API is not implemented yet, so nothing happens on confirm.`
            : ""
        }
        confirmText="Delete"
        variant="destructive"
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
}
